// team-list-helpers.ts — pure presentation helpers for the teams suite.
//
// Components that need category-grouped or interleaved team lists compose these
// over `useTeams().data`. They are NOT hooks; they are pure functions.

import { TeamCategory } from '@cabal/shared/domain/session/team/enums.js';
import type { TeamPreviewListItem } from '@cabal/shared/domain/session/team/types.js';
import { TEAM_LIST_CATEGORY, type TeamListCategory } from './TeamsProvider';

type CategoryListKey = Exclude<TeamListCategory, typeof TEAM_LIST_CATEGORY.All>;

const CATEGORY_INTERLEAVE_ORDER: readonly CategoryListKey[] = [
  TEAM_LIST_CATEGORY.Ops,
  TEAM_LIST_CATEGORY.Cortex,
  TEAM_LIST_CATEGORY.Vitals,
  TEAM_LIST_CATEGORY.Arcade,
];

const TEAM_CATEGORY_BY_LIST_CATEGORY: Record<CategoryListKey, TeamCategory> = {
  [TEAM_LIST_CATEGORY.Ops]: TeamCategory.OPS,
  [TEAM_LIST_CATEGORY.Cortex]: TeamCategory.CORTEX,
  [TEAM_LIST_CATEGORY.Vitals]: TeamCategory.VITALS,
  [TEAM_LIST_CATEGORY.Arcade]: TeamCategory.ARCADE,
};

function sortTeamsByDisplayOrder(
  teams: readonly TeamPreviewListItem[],
): TeamPreviewListItem[] {
  return [...teams].sort((a, b) => a.displayOrder - b.displayOrder);
}

function buildTeamIdsByCategory(
  teams: readonly TeamPreviewListItem[],
): Record<CategoryListKey, string[]> {
  const sorted = sortTeamsByDisplayOrder(teams);
  return CATEGORY_INTERLEAVE_ORDER.reduce<Record<CategoryListKey, string[]>>(
    (acc, category) => {
      const teamCategory = TEAM_CATEGORY_BY_LIST_CATEGORY[category];
      acc[category] = sorted.filter((team) => team.category === teamCategory).map((t) => t.id);
      return acc;
    },
    {
      [TEAM_LIST_CATEGORY.Ops]: [],
      [TEAM_LIST_CATEGORY.Cortex]: [],
      [TEAM_LIST_CATEGORY.Vitals]: [],
      [TEAM_LIST_CATEGORY.Arcade]: [],
    },
  );
}

function interleaveTeamIdsByCategory(teams: readonly TeamPreviewListItem[]): string[] {
  const idsByCategory = buildTeamIdsByCategory(teams);
  const maxLength = Math.max(
    0,
    ...CATEGORY_INTERLEAVE_ORDER.map((category) => idsByCategory[category].length),
  );
  return Array.from({ length: maxLength }).flatMap((_, index) =>
    CATEGORY_INTERLEAVE_ORDER.map((category) => idsByCategory[category][index]).filter(
      (teamId): teamId is string => Boolean(teamId),
    ),
  );
}

export function getTeamIdsForListCategory(
  teams: readonly TeamPreviewListItem[],
  category: TeamListCategory,
): string[] {
  if (category === TEAM_LIST_CATEGORY.All) {
    return interleaveTeamIdsByCategory(teams);
  }
  return buildTeamIdsByCategory(teams)[category as CategoryListKey];
}
